import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { MathJax } from "better-react-mathjax/esm";

import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from "@mui/material";

import _ from "lodash/fp";

import { useForm, useFormContext, useWatch } from "react-hook-form";

import { ControlledFormInput, PreviewContent } from "../../../components";

import {
  convertSuggestionsForDisplay,
  transformMathMlMfenced,
} from "../../../services/contentUtils";
import { useMemoCompare, useSessionStore } from "../../../services/hooks";
import {
  getPreviewTypes,
  getTaskPermissions,
  lettersArray,
  getLetter,
} from "../../../services/utils";

const PreviewCard = ({ activeTasks, objectType }) => {
  const cardName = `${_.capitalize(objectType)}PreviewCard`;

  const [workspace, workspaceStore] = useSessionStore("workspace");
  const { display = "CBT" } = useMemoCompare(
    workspace?.cards.find((c) => c.i === cardName).state || {},
  );

  const { control } = useFormContext();

  const watchedFields = {
    item: [
      "item.id",
      "item.versionID",
      "item.content",
      "item.contentSuggestions",
      "item.interactions",
    ],
    passage: [
      "passage.id",
      "passage.versionID",
      "passage.content",
      "passage.contentSuggestions",
    ],
  };
  const [objectID, versionID, content, suggestions, interactions] = useWatch({
    control,
    name: watchedFields[objectType],
  });

  const adjustedContent = useMemo(() => {
    if (!content) return content;

    const contentElement = document.createElement("div");
    contentElement.innerHTML = content;
    interactions?.forEach((interaction) => {
      const element = contentElement.querySelector(
        `.interaction[data-interaction-name="${interaction.identifier}"]`,
      );
      if (!element) return;
      const div = document.createElement("div");
      div.setAttribute("id", interaction.identifier);
      element.insertAdjacentElement("afterend", div);
    });

    return _.pipe([convertSuggestionsForDisplay, transformMathMlMfenced])(
      contentElement.innerHTML,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const permissions = useMemo(
    () => getTaskPermissions(activeTasks[objectType]),
    [activeTasks, objectType],
  );

  if (!objectID) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        "& .CBT": { display: display === "PBT" ? "none" : "inline" },
        "& .PBT": { display: display === "CBT" ? "none" : "inline" },
        "& .interaction": {
          border: 0,
          clip: "rect(0 0 0 0)",
          height: "1px",
          margin: "-1px",
          overflow: "hidden",
          padding: "0",
          position: "absolute",
          whiteSpace: "nowrap",
          width: "1px",
        },
        "& .qti-visually-hidden": { display: "none" },
        "& del": {
          backgroundColor: "rgba(229,102,134,.35)",
          borderBottom: "3px solid rgba(174,30,66,.35)",
          borderTop: "3px solid rgba(174,30,66,.35)",
          textDecoration: "line-through 3px rgba(87,15,33,.5)",
        },
        "& figure": {
          marginLeft: "0",
        },
        "& ins": {
          backgroundColor: "rgba(102,229,119,.35)",
          borderBottom: "3px solid rgba(30,174,49,.35)",
          borderTop: "3px solid rgba(30,174,49,.35)",
          textDecoration: "none",
        },
        "& table": {
          border: "solid 1px",
          borderColor: "black",
          borderCollapse: "collapse",
          borderSpacing: "0px",
          display: "in-line table",
          textIndent: "initial",
        },
        "& th, td": {
          border: "solid 1px",
          borderColor: "black",
          display: "table-cell",
          padding: "3px",
          verticalAlign: "inherit",
        },
        "& tr#perspectiveTableHeadRow th, tr#perspectiveTableHeadRow td": {
          textAlign: "left !important",
          verticalAlign: "middle",
          fontWeight: "normal",
          borderStyle: "solid",
          borderWidth: "1px",
          padding: "3px",
        },
        "& tr#perspectiveTableBodyRow th, tr#perspectiveTableBodyRow td": {
          textAlign: "left !important",
          verticalAlign: "middle",
          fontWeight: "normal",
          borderStyle: "solid",
          borderWidth: "1px",
          padding: "3px",
        },
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          alignItems: "start",
          display: "flex",
          flexDirection: "row",
          justifyContent: "end",
        }}
        variant="dense"
      >
        <PreviewContent
          objects={[{ id: objectID }]}
          objectType={_.capitalize(objectType)}
          previewTypes={getPreviewTypes(permissions)}
        />
        <ToggleButtonGroup
          exclusive
          onChange={workspaceStore.setDisplay(cardName)}
          size="small"
          value={display}
        >
          <ToggleButton value="CBT">CBT</ToggleButton>
          <ToggleButton value="PBT">PBT</ToggleButton>
        </ToggleButtonGroup>
      </Toolbar>
      <MathJax dynamic>
        <Box
          dangerouslySetInnerHTML={{
            __html: adjustedContent,
          }}
          id={`preview-${objectType}-content`}
        ></Box>
        {interactions?.map((interaction) => (
          <Interaction
            interaction={interaction}
            key={interaction.identifier}
            objectType={objectType}
            permissions={permissions}
            suggestions={suggestions}
          />
        ))}
      </MathJax>
    </Box>
  );
};

const Interaction = ({ interaction, objectType, permissions }) => {
  const { control, getValues } = useForm();
  const [container, setContainer] = useState(null);

  useEffect(() =>
    setContainer(
      document.querySelector(
        `#preview-${objectType}-content div#${interaction.identifier}`,
      ),
    ),
  );

  if (!container) return null;

  return createPortal(
    <MathJax dynamic>
      <Box
        sx={{
          "& del": {
            backgroundColor: "rgba(229,102,134,.35)",
            borderBottom: "3px solid rgba(174,30,66,.35)",
            borderTop: "3px solid rgba(174,30,66,.35)",
            textDecoration: "line-through 3px rgba(87,15,33,.5)",
          },
          "& ins": {
            backgroundColor: "rgba(102,229,119,.35)",
            borderBottom: "3px solid rgba(30,174,49,.35)",
            borderTop: "3px solid rgba(30,174,49,.35)",
            textDecoration: "none",
          },
        }}
      >
        {interaction.__typename === "ChoiceInteraction" &&
          interaction.foils.filter(({ correct }) => correct).length < 2 && (
            <RadioGroup>
              {interaction.foils.map((foil, i) => (
                <FormControlLabel
                  control={
                    <>
                      <Radio
                        checked={
                          !permissions.includes("externalReview") &&
                          foil.correct
                        }
                      />
                      <Typography>{getLetter(i)}</Typography>
                      <Typography sx={{ marginRight: 1 }}>.</Typography>
                    </>
                  }
                  key={lettersArray.at(i)}
                  label={
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: convertSuggestionsForDisplay(
                          transformMathMlMfenced(foil.content),
                        ),
                      }}
                    />
                  }
                  value={i}
                />
              ))}
            </RadioGroup>
          )}
        {interaction.__typename === "ChoiceInteraction" &&
          interaction.foils.filter(({ correct }) => correct).length >= 2 && (
            <FormGroup>
              {interaction.foils.map((foil, i) => (
                <FormControlLabel
                  control={
                    <>
                      <Checkbox
                        checked={
                          !permissions.includes("externalReview") &&
                          foil.correct
                        }
                      />
                      <Typography>{getLetter(i)}</Typography>
                      <Typography sx={{ marginRight: 1 }}>.</Typography>
                    </>
                  }
                  key={lettersArray.at(i)}
                  label={
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: convertSuggestionsForDisplay(
                          transformMathMlMfenced(foil.content),
                        ),
                      }}
                    />
                  }
                  value={i}
                />
              ))}
            </FormGroup>
          )}
        {interaction.__typename === "ExtendedTextInteraction" && (
          <>
            <ControlledFormInput
              control={control}
              fullWidth
              id={interaction.identifier}
              multiline={true}
              name={interaction.identifier}
              rows={interaction.expectedLines}
              rules={{ maxLength: interaction.expectedLength }}
            />
            <Box
              component="span"
              sx={{ display: "flex", justifyContent: "end", marginTop: "-1em" }}
            >
              <span>
                {`${getValues(interaction.identifier)?.length || 0}/${
                  interaction.expectedLength
                }`}
              </span>
            </Box>
          </>
        )}
        {interaction.__typename === "MatchTableInteraction" && (
          <>
            <Table sx={{ width: "auto" }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 200 }}>
                    {interaction.prompt}
                  </TableCell>
                  {!!interaction.columnChoices.length &&
                    interaction.columnChoices.map(({ content }) => (
                      <TableCell
                        align="center"
                        sx={{ minWidth: 200, maxWidth: 200 }}
                      >
                        {content}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!!interaction.rowChoices.length &&
                  interaction.rowChoices.map(({ content }) => (
                    <TableRow>
                      <TableCell>{content}</TableCell>
                      {interaction.columnChoices.map((cc, i) => (
                        <TableCell align="center">
                          <Radio color="primary" size="small" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </>
        )}
      </Box>
    </MathJax>,
    container,
  );
};

export { PreviewCard };
